// Traceability: FR-/AS-/EC-/SC- IDs reference
// .verified/features/dashboard-layout-editor/spec.md; ADR-022 is the grid model.

import type { LayoutItem } from "react-grid-layout/legacy";

import type { DashboardView, SectionDef, TileDef } from "./types";

/**
 * Reconcile a saved (possibly stale or corrupt) {@link DashboardView} against the
 * current section list + tile registry. Pure layout/visibility math — no I/O.
 *
 * Rules:
 *  - For each section in `sections`, every registry tile belonging to it appears.
 *    A tile keeps its saved {@link LayoutItem} (matched by `i === tile.id`) if the
 *    saved view has one; otherwise it is appended at its `defaultLayout`, row-packed
 *    left-to-right and wrapped at `cols` (default 12), starting below everything
 *    already placed (non-overlapping). This reproduces the pre-feature grid flow.
 *  - Saved-view ids that are NOT in the registry are dropped (no placeholder).
 *  - Sections present in the view but absent from `sections`/registry are ignored.
 *  - `hidden` = `view.hidden` intersected with the registry (stale ids dropped).
 *
 * Defensive (EC-03): a corrupt off-the-wire `view` (`sections: null`,
 * `hidden: undefined`, garbage) → missing/non-array shapes treated as empty, all
 * tiles fall back to defaults. Never throws.
 */
export function reconcile(
    view: DashboardView,
    sections: SectionDef[],
    registry: TileDef[],
    cols: number = 12,
): { layouts: Record<string, LayoutItem[]>; hidden: Set<string> } {
    const savedSections: Record<string, unknown> =
        view && typeof view.sections === "object" && view.sections !== null
            ? (view.sections as Record<string, unknown>)
            : {};

    const registryIds = new Set(registry.map((t) => t.id));

    const layouts: Record<string, LayoutItem[]> = {};

    for (const section of sections) {
        const sectionTiles = registry.filter((t) => t.section === section.id);

        const rawSaved = savedSections[section.id];
        const saved: LayoutItem[] = Array.isArray(rawSaved) ? (rawSaved as LayoutItem[]) : [];

        // Saved items, keyed by id, restricted to ids that still exist in the registry.
        const savedById = new Map<string, LayoutItem>();
        for (const it of saved) {
            if (it && typeof it.i === "string" && registryIds.has(it.i)) {
                savedById.set(it.i, it);
            }
        }

        // Next free row for appended (new) tiles: below the lowest saved row.
        let nextY = 0;
        for (const it of savedById.values()) {
            nextY = Math.max(nextY, (it.y ?? 0) + (it.h ?? 1));
        }

        // Row-packing cursor for appended (new) tiles: flow left-to-right and wrap
        // at `cols` instead of stacking at x:0. This reproduces the pre-feature
        // CSS-grid layout (FR-24): stats 3-up (w4×3), charts 2-up (w6×2), velocity
        // 4-up (w3×4). `rowY` starts below any saved items; `rowH` tracks the
        // tallest tile in the current row so the next wrap clears it.
        let x = 0;
        let rowY = nextY;
        let rowH = 0;

        const items: LayoutItem[] = [];
        for (const tile of sectionTiles) {
            const savedItem = savedById.get(tile.id);
            if (savedItem) {
                items.push(savedItem);
                continue;
            }
            // Absent from the saved view → append at its registry default span,
            // packed left-to-right, wrapping at the column count so it never overlaps.
            const w = tile.defaultLayout.w;
            const h = tile.defaultLayout.h;
            if (x + w > cols) {
                x = 0;
                rowY += rowH;
                rowH = 0;
            }
            items.push({
                i: tile.id,
                x,
                y: rowY,
                w,
                h,
                minW: tile.defaultLayout.minW,
            });
            x += w;
            rowH = Math.max(rowH, h);
        }

        layouts[section.id] = items;
    }

    const rawHidden = view ? view.hidden : undefined;
    const hidden = new Set<string>(
        (Array.isArray(rawHidden) ? rawHidden : []).filter((id) => registryIds.has(id)),
    );

    return { layouts, hidden };
}
